import { AlertTriangle, ChevronDown, Pencil } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { COLLECTOR_CODE, buildCategoryTree } from '../lib/build-tree'
import type { Category } from '@/api/types'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

interface CategoryTableProps {
  categories: Array<Category>
  isLoading?: boolean
  /** Article count per category id, when known. */
  counts?: Record<number, number>
  onEdit?: (category: Category) => void
  onAddChild?: (parent: Category) => void
}

export function CategoryTable({
  categories,
  isLoading,
  counts,
  onEdit,
  onAddChild,
}: CategoryTableProps) {
  const { t: tCategories } = useTranslation('categories')

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  if (categories.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {tCategories('table.empty')}
      </div>
    )
  }

  const nodes = buildCategoryTree(categories)

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{tCategories('table.group')}</TableHead>
            <TableHead>{tCategories('table.code')}</TableHead>
            <TableHead className="text-right">
              {tCategories('table.articles')}
            </TableHead>
            <TableHead className="text-right">
              {tCategories('table.sortOrder')}
            </TableHead>
            <TableHead className="text-center">
              {tCategories('table.status')}
            </TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {nodes.map(({ category, children, depth }) => {
            const isCollector = category.code === COLLECTOR_CODE
            const isMainGroup = depth === 0

            return (
              <TableRow
                key={category.id}
                className={cn(isCollector && 'bg-amber-50 hover:bg-amber-50')}
              >
                <TableCell>
                  <div
                    className="flex items-center gap-2"
                    // Indent per level rather than per "is a child": a deeper level should not
                    // exist, but if it ever does it has to render somewhere sensible.
                    style={{ paddingLeft: `${depth * 24}px` }}
                  >
                    {isCollector ? (
                      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-700" />
                    ) : children.length > 0 ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <span className="w-4 shrink-0" />
                    )}
                    <span
                      className={cn(
                        isMainGroup ? 'font-semibold' : 'text-muted-foreground',
                        isCollector && 'text-amber-900',
                      )}
                    >
                      {category.name}
                    </span>
                    {isMainGroup && !isCollector && children.length === 0 && (
                      <Badge variant="outline" className="font-normal">
                        {tCategories('table.noSubgroups')}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm text-muted-foreground">
                  {category.code}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {counts?.[category.id] ?? '–'}
                </TableCell>
                {/* Visible because it decides the order of every list a guest sees, and until now
                    it could only be read by opening each group's edit dialog one at a time —
                    which is exactly the wrong way round for a value whose whole point is how the
                    rows compare to each other. Siblings are ordered by it, so a subgroup's number
                    is only ever compared with its siblings', never with a main group's. */}
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {category.sortOrder}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={category.active ? 'default' : 'secondary'}>
                    {category.active
                      ? tCategories('table.active')
                      : tCategories('table.inactive')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    {isMainGroup && !isCollector && onAddChild && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onAddChild(category)}
                      >
                        {tCategories('table.addSubgroup')}
                      </Button>
                    )}
                    {onEdit && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={tCategories('table.edit')}
                        onClick={() => onEdit(category)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
